import React, { useMemo } from "react";
import {Card, CardHeader, CardContent, Typography, IconButton, Chip, Box, Skeleton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import QrCodeIcon from "@mui/icons-material/QrCode";
import './Checkpoint.css'
import { capitalizeCamelCase, capitalizeSentence, findDeepValue, flattenObj, formatDate, isFilePath, parseUnderscore } from "../../services/functions";
import FilePreview from "../utils/FilePreview";
import theme, { buttonClasses } from "../utils/theme";
import { Button } from "../utils";
import { DefaultLocation, GateIn, Weighing } from "../utils/Genericstats";

/* Calculate duration between two dates*/
export function calculateDuration(startDate, endDate) {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    const diffMs = Math.abs(end - start);
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let formatted = "";
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0 || hours > 0) formatted += `${minutes}m `;
    formatted += `${seconds}s`;

    return { hours, minutes, seconds, totalSeconds, formatted: formatted.trim() };
}

/*Format total seconds into hours, minutes, seconds object*/
function formatTotalSeconds(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let formatted = "";
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0 || hours > 0) formatted += `${minutes}m `;
    formatted += `${seconds}s`;

    return { hours, minutes, seconds, totalSeconds, formatted: formatted.trim() };
}

/* Get checkpoint icon based on checkpoint type or name */
function getCheckpointIcon(displayName, checkpointType) {
    const name = displayName?.toLowerCase() || "";
    const type = checkpointType?.toLowerCase() || "";

    if (name.includes("gate in") || name.includes("entry")) {
        return <GateIn />;
    }
    if (name.includes("gate out") || name.includes("exit")) {
        return <GateIn />;
    }
    if (name.includes("weigh") || name.includes("wb")) {
        return <Weighing sx={{ fontSize: 20 }} />;
    }
    if (type === "bay" || name.includes("dock") || name.includes("bay")) {
        return <LocalShippingIcon sx={{ fontSize: 20 }} />;
    }
    if (type === "access") {
        return <AccessTimeIcon sx={{ fontSize: 20 }} />;
    }
    return <DefaultLocation />;
}

/* Get status chip color*/
function getStatusColor(status) {
    const s = status?.toLowerCase() || "";
    if (s === "completed" || s === "complete") return { bg: "#e8f5e9", color: "#2e7d32" };
    if (s === "ongoing" || s === "in progress") return { bg: "#fff3e0", color: "#ef6c00" };
    if (s === "pending") return { bg: "#fce4ec", color: "#c62828" };
    return { bg: "#e3f2fd", color: "#1565c0" };
}

/* Single checkpoint card in the timeline*/
function CheckpointCard({
    checkpoint,
    isExpanded = true,
    checkpointColor = "#ef6c00",
    durationAtCheckpoint = null,
}) {
    const { displayName, inTime, outTime, checkpointType, formData, isOfflinePacket, entryType } = checkpoint;
    //  ADDING THIS: Only show duration if it's greater than 0 seconds
    const shouldShowDuration = durationAtCheckpoint && durationAtCheckpoint.totalSeconds > 0;
    return (
        <Box sx={{
            backgroundColor: "#fff",
            border: `1px solid ${checkpointColor}20`,
            borderLeft: `4px solid ${checkpointColor}`,
            borderRadius: "8px",
            p: 1.5,
            position: "relative",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}>
            {/* Header with name and icons */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
            }}>
                <Typography sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: checkpointColor,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}>
                    {displayName}
                    {isOfflinePacket && (
                        <Chip
                            label="Offline"
                            size="small"
                            sx={{
                                fontSize: "10px",
                                height: "18px",
                                backgroundColor: "#fff3e0",
                                color: "#ef6c00",
                            }}
                        />
                    )}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    {/* Duration at checkpoint badge  IT will be visible on ui only when duration is greater than 0 sec*/}
                    {durationAtCheckpoint && shouldShowDuration  && (
                        <Chip
                            label={durationAtCheckpoint.formatted}
                            size="small"
                            sx={{
                                fontSize: "10px",
                                height: "20px",
                                backgroundColor: `${checkpointColor}15`,
                                color: checkpointColor,
                                fontWeight: 600,
                            }}
                        />
                    )}
                    {entryType === "camera" && (
                        <IconButton size="small" sx={{ p: 0.5 }}>
                            <CameraAltIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                        </IconButton>
                    )}
                    {entryType === "scan" && (
                        <IconButton size="small" sx={{ p: 0.5 }}>
                            <SettingsInputAntennaIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                        </IconButton>
                    )}
                    {entryType === "manual" && (
                        <IconButton size="small" sx={{ p: 0.5 }}>
                            <EditIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Time rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {checkpoint.approvalStatus === "RejectToGateout" ? null : (inTime && (
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            {checkpoint.inTimeLabel || (checkpointType === "access" ? "Arrived" : checkpointType === "bay" ? "Arrived" : "In Time")}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#333", fontWeight: 500 }}>
                            {formatDate(inTime)}
                        </Typography>
                    </Box>
                ))}
                {checkpoint.approvalStatus === "RejectToGateout" ? null : (outTime && (
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            {checkpoint.outTimeLabel || (checkpointType === "access" ? "Exit" : checkpointType === "bay" ? "Exit" : "Out Time")}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#333", fontWeight: 500 }}>
                            {formatDate(outTime)}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {checkpoint.blocks?.map((block, i) => (
                <Box key={i} sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#333",
                            mb: 1,
                            textAlign: "center",
                        }}
                    >
                        {capitalizeSentence(block.sectionType)}
                    </Typography>

                    {block.rows.map((row, j) => {
                        let value = row.value;

                        if (
                            value === null ||
                            value === undefined ||
                            value === "" ||
                            value === "-" ||
                            (Array.isArray(value) && value.length === 0)
                        ) {
                            return null;
                        }

                        const isoPattern =
                            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
                        if (typeof value === "string" && isoPattern.test(value)) {
                            value = formatDate(value);
                        }

                        return (
                            <Box
                                key={j}
                                sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
                            >
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    {row.label}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: "#333",
                                        fontWeight: 500,
                                        maxWidth: "60%",
                                        textAlign: "right",
                                    }}
                                >
                                    {Array.isArray(value) ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
                                            {value.map((el, k) =>
                                                isFilePath(el) ? (
                                                    <FilePreview
                                                        key={k}
                                                        fileKey={el}
                                                        extension={String(el).split(".").pop()}
                                                    />
                                                ) : (
                                                    <span key={k}>{capitalizeCamelCase(String(el))}</span>
                                                )
                                            )}
                                        </Box>
                                    ) : isFilePath(value) ? (
                                        <FilePreview
                                            fileKey={value}
                                            extension={String(value).split(".").pop()}
                                        />
                                    ) : (
                                        capitalizeCamelCase(String(value))
                                    )}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}

/* Journey Breakdown component showing time between checkpoints */
function JourneyBreakdown({ breakdownItems }) {
    const filteredItems = breakdownItems?.filter(item => {
        const duration = item.duration || "";
        return duration !== "0s" && duration !== "";
    }) || [];

    // ✅ CHANGE THIS: Check filteredItems instead of breakdownItems
    if (filteredItems.length === 0) return null;
    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fff",
                borderRadius: "12px",
                border: "1px solid var(--primary-color)",
                borderLeft: "4px solid var(--primary-color)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            }}
        >
            <Typography
                sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "var(--primary-color)",
                    letterSpacing: "0.5px",
                    mb: 1.5,
                }}
            >
                Journey Breakdown
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    pb: 1,
                }}
            >
                {filteredItems.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            flex: "1 1 auto",
                            width: "auto",
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            p: 1,
                            textAlign: "center",
                            border: "1px solid var(--primary-color)",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "10px",
                                color: "#666",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {item.label}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 700,
                                mt: 0.5,
                                color: "var(--primary-color)",
                            }}
                        >
                            {item.duration}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/* Process checkpoint data for timeline display
 * FIXED DURATION CALCULATION:
 * Total Journey Time = Sum of:
 *   1. Time AT each checkpoint (inTime → outTime)
 *   2. Time BETWEEN checkpoints (current outTime → next inTime)
 */
export function processCheckpointsForTimeline(data, historicalDataHeaderMap = {}, inTimeOutTimeConfig = {}) {
    if (!data || !data.statusHistory) return { checkpoints: [], breakdownItems: [], totalDuration: null, durations: [] };

    const history = [...data.statusHistory];
    const eventData = data.data ? [...data.data] : [];
    const checkpoints = [];

    // Process each checkpoint
    history.forEach((event, index) => {
        const { displayName, outTime, inTime, checkpointType, checkpointId, _basis, type, approvalStatus } = event;
        const isOfflinePacket = _basis === "offline-packet";
        const { hideInTime = false, hideOutTime = false, inTimeLabel = "In Time", outTimeLabel = "Out Time" } = inTimeOutTimeConfig[checkpointId] || {};

        let blocks = [];

        while (
            eventData.length &&
            (
                eventData[0]._id === event.checkInDataID ||
                eventData[0]._id === event.checkOutDataID
            )
        ) {
            let currentData = eventData.shift();
            let type = currentData.type;

            let rows = [];
            const currentTableHeader = historicalDataHeaderMap[`${currentData.checkpointId}-${type}`];

            if (currentTableHeader) {
                currentTableHeader.forEach((header) => {
                    let value = findDeepValue(currentData, header.name?.split("."));
                    if (value == "-") value = findDeepValue(currentData.formData, header.name?.split("."));
                    if (value) {
                        rows.push({
                            label: header.label,
                            value
                        });
                    }
                });
            } else {
                let processedData = {};
                flattenObj(processedData, currentData.formData);
                delete processedData.vehicleId;

                for (let key in processedData) {
                    if (processedData[key]) {
                        rows.push({
                            label: capitalizeSentence(capitalizeCamelCase(parseUnderscore(key))),
                            value: processedData[key]
                        });
                    }
                }
            }

            const cleanedRows = rows.filter(r => {
                const v = r.value;
                return !(
                    v === null ||
                    v === undefined ||
                    v === "" ||
                    v === "-" ||
                    (Array.isArray(v) && v.length === 0)
                );
            });

            if (cleanedRows.length) {
                blocks.push({
                    sectionType: type,
                    rows: cleanedRows
                });
            }
        }

        checkpoints.push({
            displayName,
            inTime: hideInTime ? null : inTime,
            outTime: hideOutTime ? null : outTime,
            inTimeLabel,
            outTimeLabel,
            checkpointType,
            checkpointId,
            isOfflinePacket,
            formData: {},
            formDataType: "",
            originalIndex: index,
            entryType: type,
            blocks,
            approvalStatus
        });
    });

    // ============================================
    // FIXED DURATION CALCULATION
    // ============================================
    // We need to calculate durations such that their sum equals total journey time
    // 
    // Total Journey = firstCheckpoint.inTime → lastCheckpoint.outTime
    // 
    // This breaks down into:
    // 1. Time AT checkpoint 1 (inTime → outTime)
    // 2. Time FROM checkpoint 1 TO checkpoint 2 (cp1.outTime → cp2.inTime)
    // 3. Time AT checkpoint 2 (inTime → outTime)
    // 4. Time FROM checkpoint 2 TO checkpoint 3 (cp2.outTime → cp3.inTime)
    // ... and so on

    const durations = []; // Array to store all duration segments
    const breakdownItems = [];

    for (let i = 0; i < checkpoints.length; i++) {
        const current = checkpoints[i];
        const next = checkpoints[i + 1];

        // 1. Duration AT this checkpoint (inTime → outTime)
        let durationAtCheckpoint = null;
        if (current.inTime && current.outTime) {
            durationAtCheckpoint = calculateDuration(current.inTime, current.outTime);
            if (durationAtCheckpoint && durationAtCheckpoint.totalSeconds > 0) {
                durations.push({
                    type: 'at',
                    checkpointIndex: i,
                    checkpointName: current.displayName,
                    duration: durationAtCheckpoint
                });

                breakdownItems.push({
                    label: `At ${current.displayName}`,
                    duration: durationAtCheckpoint.formatted,
                    isAtCheckpoint: true,
                    isHighlight: false,
                });
            }
        }

        // Store duration at checkpoint for display in the card
        current.durationAtCheckpoint = durationAtCheckpoint;

        // 2. Duration TO next checkpoint (current.outTime → next.inTime)
        if (next) {
            const endOfCurrent = current.outTime || current.inTime;
            const startOfNext = next.inTime || next.outTime;

            if (endOfCurrent && startOfNext) {
                const durationBetween = calculateDuration(endOfCurrent, startOfNext);
                if (durationBetween && durationBetween.totalSeconds > 0) {
                    durations.push({
                        type: 'between',
                        fromIndex: i,
                        toIndex: i + 1,
                        fromName: current.displayName,
                        toName: next.displayName,
                        duration: durationBetween
                    });

                    breakdownItems.push({
                        label: `${current.displayName} → ${next.displayName}`,
                        duration: durationBetween.formatted,
                        isAtCheckpoint: false,
                        isHighlight: false,
                    });

                    // Store for timeline connector display
                    current.durationToNext = durationBetween;
                }
            }
        }
    }

    // Mark the last breakdown item as highlight (total)
    if (breakdownItems.length > 0) {
        breakdownItems[breakdownItems.length - 1].isHighlight = true;
    }

    // Calculate total journey duration by summing all duration segments
    // This ensures total = sum of all parts
    let totalSeconds = 0;
    durations.forEach(d => {
        totalSeconds += d.duration.totalSeconds;
    });

    const totalDuration = totalSeconds > 0 ? formatTotalSeconds(totalSeconds) : null;

    // Also calculate the direct total for verification
    let directTotalDuration = null;
    if (checkpoints.length > 0) {
        const firstCheckpoint = checkpoints[0];
        const lastCheckpoint = checkpoints[checkpoints.length - 1];
        const startTime = firstCheckpoint.inTime || firstCheckpoint.outTime;
        const endTime = lastCheckpoint.outTime || lastCheckpoint.inTime;
        directTotalDuration = calculateDuration(startTime, endTime);
    }

    // Use the direct calculation for display (it's more accurate)
    // But now the breakdown should match!
    return {
        checkpoints,
        breakdownItems,
        totalDuration: directTotalDuration,
        durations,
        calculatedTotalSeconds: totalSeconds
    };
}

/*MAIN VEHICLE JOUNEY DETAILED COMPONENT */
function VehicleJourneyDetailCard({
    isOpen,
    onClose,
    data,
    tableHeader = [],
    historicalDataHeaderMap = {},
    inTimeOutTimeConfig = {},
    handlePrint,
    customizeTripWorkflow = false,
    handleCustomizeTripWorkflow,
    printLabelPerm = false,
    height = "75vh",
}) {

    // Process data for display
    const { checkpoints, breakdownItems, totalDuration, calculatedTotalSeconds } = useMemo(() =>
        processCheckpointsForTimeline(data, historicalDataHeaderMap, inTimeOutTimeConfig),
        [data, historicalDataHeaderMap, inTimeOutTimeConfig]
    );

    // Extract header details from tableHeader
    const headerDetails = useMemo(() => {
        if (!data || !tableHeader) return [];
        return tableHeader.map(header => ({
            label: header.label,
            name: header.name,
            value: header.type === "date"
                ? formatDate(findDeepValue(data, header.name.split(".")))
                : findDeepValue(data, header.name.split("."))
        }));
    }, [data, tableHeader]);

    // Get vehicle number and trip info
    const vehicleNumber = data?.vehicleNumber || "N/A";
    const tripId = data?.trip_counter || "";
    const warehouse = data?.plantName || "";
    const tripStatus = data?.trip_status || "-";
    const remarks = data?.remarks || "-";
    const statusColors = getStatusColor(tripStatus);
    if (!isOpen) return null;

    return (
        <div className={`detail_card ${isOpen ? "open" : ""}`} >
            <Card sx={{
                borderRadius: "12px",
                border: `1px solid ${theme?.themeOrange || "#ef6c00"}`,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}>
                {/* Header */}
                <CardHeader
                    sx={{
                        height: "auto",
                        minHeight: "50px",
                        backgroundColor: theme?.themeOrange || "#ef6c00",
                        py: 1.5,
                        px: 2,
                    }}
                    title={
                        <Typography sx={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: 600,
                        }}>
                            Journey Detail - {vehicleNumber}
                        </Typography>
                    }
                    action={
                        <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
                            <CloseIcon />
                        </IconButton>
                    }
                />

                <CardContent sx={{
                    height,
                    overflowY: "auto",
                    overflowX: "hidden",
                    backgroundColor: "#f8f9fa",
                    p: 2,
                }}>
                    {/* Vehicle Info Section */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                        p: 1.5,
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                        border: "1px solid var(--primary-color)",
                        borderLeft: `4px solid var(--primary-color)`,

                    }}>
                        <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        }}>
                            <LocalShippingIcon sx={{ color: "var(--primary-color)", fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                                    {vehicleNumber}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    {warehouse}
                                </Typography>
                            </Box>
                            <Chip
                                label={capitalizeCamelCase(tripStatus)}
                                size="small"
                                sx={{
                                    backgroundColor: statusColors.bg,
                                    color: statusColors.color,
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    height: "22px",
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Trip ID and Remarks */}
                    {(remarks !== "-") && (
                        <Box sx={{ mb: 2, px: 0.5 }}>

                            {remarks && remarks !== "-" && (
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    Remarks: {remarks}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Action Buttons */}
                    {(printLabelPerm || customizeTripWorkflow) && (
                        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                            {printLabelPerm && (
                                <Button
                                    onClick={handlePrint}
                                    style={{ ...buttonClasses.lynkitOrangeEmpty }}
                                >
                                    <QrCodeIcon />Print Label
                                </Button>
                            )}
                            {customizeTripWorkflow && (
                                <Button
                                    onClick={handleCustomizeTripWorkflow}
                                    style={{ ...buttonClasses.lynkitOrangeEmpty }}
                                >
                                    Customize Trip Workflow
                                </Button>
                            )}
                        </Box>
                    )}

                    {/* Total Journey Summary */}
                    {totalDuration && (
                        <Box sx={{
                            backgroundColor: "#FFF",
                            borderRadius: "12px",
                            p: 2,
                            mb: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            border: "1px solid var(--primary-color)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                            borderLeft: "4px solid var(--primary-color)",
                        }}>
                            <Box>
                                <Typography sx={{ fontSize: "11px", color: "var(--primary-color)" }}>
                                    {tripId && (
                                        <>
                                            <Typography sx={{ fontSize: "12px", color: "var(--primary-color)" }}>
                                                Trip ID:
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: "20px",
                                                fontWeight: 700,
                                                color: "var(--primary-color)",
                                                display: "flex",
                                                alignItems: "baseline",
                                                gap: 0.5,
                                            }}>
                                                {tripId}
                                            </Typography>
                                        </>
                                    )}

                                </Typography>

                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                                <Typography sx={{ fontSize: "11px", color: "var(--primary-color)" }}>
                                    Total Journey Time
                                </Typography>
                                <Typography sx={{
                                    fontSize: "20px",
                                    fontWeight: 700,
                                    color: "var(--primary-color)",
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 0.5,
                                }}>
                                    {totalDuration.hours > 0 && <><span>{totalDuration.hours}h</span></>}
                                    {totalDuration.minutes > 0 && <><span>{totalDuration.minutes}m</span></>}
                                    <span>{totalDuration.seconds}s</span>
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {(() => {
                        const filteredDetails = headerDetails
                            .filter(item => {
                                return ![
                                    "vehicleNumber",
                                    "trip_counter",
                                    "trip_status",
                                    "plantName",
                                ].includes(item.name);
                            })
                            .filter(item => item.value && item.value !== "-");

                        if (!filteredDetails.length) return null; // 👈 no data → no border, no box

                        return (
                            <Box
                                sx={{
                                    backgroundColor: "#fff",
                                    borderRadius: "8px",
                                    p: 1.5,
                                    mb: 2,
                                    border: "1px solid #e0e0e0",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                }}
                            >
                                {filteredDetails.map((item, i) => (
                                    <Box
                                        key={i}
                                        sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
                                    >
                                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                            {item.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        );
                    })()}

                    {/* Asset Details (if present) */}
                    {data?.mapCount !== undefined && data?.scanCount !== undefined && (
                        <Box sx={{
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            p: 2,
                            mb: 2,
                            border: "1px solid #e0e0e0",
                        }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 1.5, textAlign: "center" }}>
                                Asset Details
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>Assets Mapped</Typography>
                                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{data.mapCount}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>Assets Scanned</Typography>
                                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{data.scanCount}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>Assets Remaining</Typography>
                                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: data.mapCount - data.scanCount > 0 ? "#c62828" : "#2e7d32" }}>
                                    {data.mapCount - data.scanCount}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Timeline */}
                    <Box sx={{ position: "relative" }}>
                        {checkpoints.map((checkpoint, index) => {
                            console.log('checkpoint===>>>>', checkpoint)
                            const checkpointColors = [
                                "#ff7700", // orange
                                "#1976d2", // blue
                                "#388e3c", // green
                                "#7b1fa2", // purple
                                // "#c62828", // red
                            ];
                            const color = checkpoint.approvalStatus === "RejectToGateout" ?  "#c62828" : checkpointColors[index % checkpointColors.length];

                            return (
                                <Box key={index} sx={{ display: "flex", gap: 1.5, mb: index < checkpoints.length - 1 ? 0 : 0 }}>
                                    {/* Timeline indicator */}
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        minWidth: "40px",
                                    }}>
                                        {/* Icon circle */}
                                        <Box sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "50%",
                                            backgroundColor: color,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#fff",
                                            flexShrink: 0,
                                        }}>
                                            {checkpoint.approvalStatus === "RejectToGateout" ? <CloseIcon ontSize={"small"} htmlColor={"#fff"}/> : getCheckpointIcon(checkpoint.displayName, checkpoint.checkpointType)}
                                        </Box>
                                        {/* Connector line with duration TO NEXT checkpoint */}
                                        {index < checkpoints.length - 1 && (
                                            <Box sx={{
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                minHeight: "60px",
                                                position: "relative",
                                            }}>
                                                <Box sx={{
                                                    width: "2px",
                                                    flex: 1,
                                                    backgroundColor: "#e0e0e0",
                                                }} />
                                                {/* Duration BETWEEN checkpoints (transit time) */}
                                                {checkpoint.durationToNext && (
                                                    <Typography sx={{
                                                        position: "absolute",
                                                        left: "50%",
                                                        top: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        fontSize: "10px",
                                                        color: "#ef6c00",
                                                        fontWeight: 600,
                                                        backgroundColor: "#fff",
                                                        px: 0.5,
                                                        whiteSpace: "nowrap",
                                                        border: "1px solid #ef6c00",
                                                        borderRadius: "4px",
                                                    }}>
                                                        {checkpoint.durationToNext.formatted}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Checkpoint card */}
                                    <Box sx={{ flex: 1, pb: index < checkpoints.length - 1 ? 2 : 0 }}>
                                        <CheckpointCard
                                            checkpoint={checkpoint}
                                            checkpointColor={color}
                                            durationAtCheckpoint={checkpoint.durationAtCheckpoint}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Journey Breakdown */}
                    <JourneyBreakdown breakdownItems={breakdownItems} />
                </CardContent>
            </Card>
        </div>
    );
}

export default VehicleJourneyDetailCard;




// SHOWING ONLY THE SKELETION INSTEAD OF LOADER
function VehicleInfoSkeleton() {
    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            p: 1.5,
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            border: "1px solid var(--primary-color)",
            borderLeft: "4px solid var(--primary-color)",
        }}>
            <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: "8px" }} />
            <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Skeleton variant="text" width={120} height={20} />
                    <Skeleton variant="text" width={100} height={16} />
                </Box>
                <Skeleton variant="rounded" width={80} height={22} />
            </Box>
        </Box>
    );
}

function TotalJourneySkeleton() {
    return (
        <Box sx={{
            backgroundColor: "#FFF",
            borderRadius: "12px",
            p: 2,
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid var(--primary-color)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            borderLeft: "4px solid var(--primary-color)",
        }}>
            <Box>
                <Skeleton variant="text" width={60} height={14} />
                <Skeleton variant="text" width={100} height={28} sx={{ mt: 0.5 }} />
            </Box>
            <Box sx={{ textAlign: "right" }}>
                <Skeleton variant="text" width={120} height={14} />
                <Skeleton variant="text" width={80} height={28} sx={{ mt: 0.5 }} />
            </Box>
        </Box>
    );
}

function CheckpointCardSkeleton({ color }) {
    return (
        <Box sx={{
            backgroundColor: "#fff",
            border: `1px solid ${color}20`,
            borderLeft: `4px solid ${color}`,
            borderRadius: "8px",
            p: 1.5,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
            }}>
                <Skeleton variant="text" width={150} height={20} />
                <Skeleton variant="rounded" width={60} height={20} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={120} height={16} />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={120} height={16} />
                </Box>
            </Box>
        </Box>
    );
}

function TimelineSkeletons() {
    const checkpointColors = ["#ff7700", "#1976d2", "#388e3c", "#7b1fa2"];

    return (
        <Box sx={{ position: "relative" }}>
            {checkpointColors.map((color, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1.5, mb: index < checkpointColors.length - 1 ? 2 : 0 }}>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: "40px",
                    }}>
                        <Skeleton
                            variant="circular"
                            width={36}
                            height={36}
                            sx={{ backgroundColor: `${color}40` }}
                        />
                        {index < checkpointColors.length - 1 && (
                            <Box sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                minHeight: "60px",
                            }}>
                                <Box sx={{
                                    width: "2px",
                                    flex: 1,
                                    backgroundColor: "#e0e0e0",
                                    minHeight: "60px",
                                }} />
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, pb: index < checkpointColors.length - 1 ? 2 : 0 }}>
                        <CheckpointCardSkeleton color={color} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

export function VehicleJourneyDetailSkeleton({ isOpen, onClose, height = "75vh" }) {
    if (!isOpen) return null;

    return (
        <div className="detail_card open">
            <Card sx={{
                borderRadius: "12px",
                border: `1px solid ${theme?.themeOrange || "#ef6c00"}`,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}>
                <CardHeader
                    sx={{
                        height: "auto",
                        minHeight: "50px",
                        backgroundColor: theme?.themeOrange || "#ef6c00",
                        py: 1.5,
                        px: 2,
                    }}
                    title={
                        <Typography sx={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: 600,
                        }}>
                            Journey Detail - Loading...
                        </Typography>
                    }
                    action={
                        <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
                            <CloseIcon />
                        </IconButton>
                    }
                />

                <CardContent sx={{
                    height,
                    overflowY: "auto",
                    overflowX: "hidden",
                    backgroundColor: "#f8f9fa",
                    p: 2,
                }}>
                    <VehicleInfoSkeleton />
                    <TotalJourneySkeleton />
                    <TimelineSkeletons />
                </CardContent>
            </Card>
        </div>
    );
}